import { apiClient, idempotentConfig, unwrap } from './client';
import type {
  ApiEnvelope,
  Approval,
  ApprovalDecisionResult,
  ApprovalState,
} from './types';

/**
 * Maker-checker approvals (PS-2 cross-track contract). Track A builds these in parallel; the SPA
 * degrades gracefully (normalizeApiError → actionable empty state) if they are not live yet.
 *
 * The approver CANNOT be the proposer: a same-actor decision is rejected `403 SAME_ACTOR`. When the
 * required simulation evidence is missing the decision is `409 GATE_UNSATISFIED` with `missing[]`.
 */

const enc = (s: string) => encodeURIComponent(s);

/** POST /v1/flows/{flowId}/approvals — submit a draft version for approval. */
export async function submitForApproval(
  flowId: string,
  versionId: number,
  note?: string,
  idemKey?: string,
): Promise<Approval> {
  const { data } = await apiClient.post<ApiEnvelope<Approval>>(
    `/v1/flows/${enc(flowId)}/approvals`,
    { versionId, note },
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** GET /v1/flows/{flowId}/approvals?state= — list approvals for a flow. */
export async function listApprovals(flowId: string, state?: ApprovalState): Promise<Approval[]> {
  const { data } = await apiClient.get<ApiEnvelope<Approval[]>>(
    `/v1/flows/${enc(flowId)}/approvals`,
    { params: { state } },
  );
  return unwrap(data);
}

/** POST /v1/flows/{flowId}/approvals/{approvalId}/decision — approve/reject (role-gated, ≠ proposer). */
export async function decideApproval(
  flowId: string,
  approvalId: string,
  decision: 'APPROVE' | 'REJECT',
  opts: { note?: string; impactReportId?: string; idemKey?: string } = {},
): Promise<ApprovalDecisionResult> {
  const { data } = await apiClient.post<ApiEnvelope<ApprovalDecisionResult>>(
    `/v1/flows/${enc(flowId)}/approvals/${enc(approvalId)}/decision`,
    { decision, note: opts.note, impactReportId: opts.impactReportId },
    idempotentConfig(opts.idemKey),
  );
  return unwrap(data);
}
